import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import axios from 'axios';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Editor, EditorState, convertToRaw, convertFromRaw, RichUtils, ContentState, Modifier } from 'draft-js';
import 'draft-js/dist/Draft.css';
import ArtifactViewer from '../ArtifactViewer/ArtifactViewer';
import PdfThumbnail from '../ArtifactViewer/PdfThumbnail';
import AmplifierTemplateComponents from './AmplifierTemplateComponents';
import AmpPartsList from './AmpPartsList';
import AmpDiscussion from './AmpDiscussion';
import WhereToBuy from './WhereToBuy';
import SocialShareBar from '../SocialShareBar/SocialShareBar';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import WikiSuggestEdit from '../WikiSuggestEdit/WikiSuggestEdit';
import { WikiBadge } from '../ContributorsScreen/ContributorsScreen';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { linkifyHtml } from '../../utils/linkify';
import '../../styles/ampDescriptionDisplay.css';
import './AmplifierDetailsScreen.css';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const ampApi = axios.create({ baseURL: API_URL });
const artifactApi = axios.create({ baseURL: API_URL });

// Helper: extract YouTube video ID from any YouTube URL format
function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

const AmplifierDetailsScreen = () => {
  const { isReadOnly, isUnauthenticatedAmplifierAccess, token, isAuthenticated, user, isAdmin } = useAuth();
  const { amplifierId, manufacturerSlug, ampSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [amplifier, setAmplifier] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadArtifactType, setUploadArtifactType] = useState('photo');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [artifactDragOver, setArtifactDragOver] = useState(false);
  const [artifactTab, setArtifactTab] = useState('all');
  const [imageParamHandled, setImageParamHandled] = useState(false);

  // Derive the effective amplifier ID — always starts null so secondary requests
  // (children, artifacts, collections, etc.) wait until fetchAmplifier completes.
  // This makes ID-based routes (child amps) behave identically to slug-based
  // routes (parent amps), which naturally start with null.
  const [resolvedAmplifierId, setResolvedAmplifierId] = useState(null);
  const [ampFetchError, setAmpFetchError] = useState(null);
  const [ampFetchRetryKey, setAmpFetchRetryKey] = useState(0);

  // Child amplifier management state
  const [children, setChildren] = useState([]);

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(-1);

  // Inline editing state
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [infoFormData, setInfoFormData] = useState({});
  
  // Enhanced description editor state (similar to AmplifierForm)
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [fontSize, setFontSize] = useState(14);
  const [activeTab, setActiveTab] = useState('visual'); // 'visual' or 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [useRawHtmlMode, setUseRawHtmlMode] = useState(false);
  const [htmlDirty, setHtmlDirty] = useState(false);
  
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);

  // Parts list review deep-link
  const partsListRef = React.useRef(null);
  const reviewPartsParam = searchParams.get('review_parts');

  // Wiki sections state (community-contributed sections beyond the description)
  const [wikiSections, setWikiSections] = useState({});
  const [wikiSectionsExpanded, setWikiSectionsExpanded] = useState({});
  // Unified amp edit history (all wiki sections + parts list changes)
  const [showAmpHistory, setShowAmpHistory] = useState(false);
  const [ampHistory, setAmpHistory] = useState([]);
  const [ampHistoryLoading, setAmpHistoryLoading] = useState(false);
  // Lightbox for wiki artifact images (data-artifact-id thumbnails clicked in description)
  const [lightboxArtifactId, setLightboxArtifactId] = useState(null);

  // Delegate-click handler for artifact thumbnails embedded in wiki HTML.
  // Catches clicks on <img data-artifact-id="..."> inside any .amp-description-display div
  // and opens the full-size image in a lightbox without needing per-image listeners.
  const handleWikiImageClick = (e) => {
    const img = e.target.closest('img[data-artifact-id]');
    if (img) {
      e.stopPropagation();
      setLightboxArtifactId(img.getAttribute('data-artifact-id'));
    }
  };

  // Pending review approve state (admin only)
  const [approvingAmp, setApprovingAmp] = useState(false);
  const [deletingAmp, setDeletingAmp] = useState(false);

  // Wishlist / Collection state
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistCollectionId, setWishlistCollectionId] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [ownedChildId, setOwnedChildId] = useState(null);

  // Watch/Subscribe state
  const [isWatchingAmp, setIsWatchingAmp] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);

  // Amplifier details are always read-only for unauthenticated users or users with read-only permissions
  // Both /amps/:slug/:slug and /amplifier-components/:id render this same component,
  // so we check permissions against the canonical screen path only
  // Exception: admins and owners of collection child amps can always edit
  const isOwner = isAuthenticated && amplifier?.owner_user_id && user?.user_id && amplifier.owner_user_id === user.user_id;
  const readOnlyMode = (isAdmin || isOwner) ? false : (isUnauthenticatedAmplifierAccess() || isReadOnly('/amplifier-components/:amplifierId'));

  // Check if this amp is in the user's wishlist or owned
  // Gate on `amplifier` so these don't fire until the main fetch completes —
  // avoids a request flood that stalls the browser on ID-based (child) routes.
  useEffect(() => {
    if (!isAuthenticated || !resolvedAmplifierId || !amplifier) return;
    const checkCollection = async () => {
      try {
        const resp = await fetch(`${API_URL}/collections/check/${resolvedAmplifierId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) { resp.body?.cancel(); return; }
        {
          const data = await resp.json();
          if (data.in_collection && data.is_wishlist) {
            setIsWishlisted(true);
            setWishlistCollectionId(data.collection_id);
            setOwnedChildId(null);
          } else if (data.in_collection && !data.is_wishlist) {
            setIsWishlisted(false);
            setWishlistCollectionId(null);
            setOwnedChildId(data.owned_child_id || data.amplifier_id);
          } else {
            setIsWishlisted(false);
            setWishlistCollectionId(null);
            setOwnedChildId(null);
          }
        }
      } catch (_) { /* silent */ }
    };
    checkCollection();
  }, [isAuthenticated, resolvedAmplifierId, token, amplifier]);

  // Check if user is watching this amplifier
  useEffect(() => {
    if (!isAuthenticated || !resolvedAmplifierId || !amplifier) return;
    const checkWatchStatus = async () => {
      try {
        const resp = await fetch(`${API_URL}/subscriptions/check?entity_type=amplifier&entity_id=${resolvedAmplifierId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) { resp.body?.cancel(); return; }
        const data = await resp.json();
        setIsWatchingAmp(data.subscribed);
      } catch (e) { /* silent */ }
    };
    checkWatchStatus();
  }, [isAuthenticated, resolvedAmplifierId, token, amplifier]);

  const toggleWatchAmp = async () => {
    if (!isAuthenticated || watchLoading) return;
    setWatchLoading(true);
    try {
      if (isWatchingAmp) {
        await fetch(`${API_URL}/subscriptions`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ entity_type: 'amplifier', entity_id: parseInt(resolvedAmplifierId) })
        });
        setIsWatchingAmp(false);
      } else {
        await fetch(`${API_URL}/subscriptions`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ entity_type: 'amplifier', entity_id: parseInt(resolvedAmplifierId) })
        });
        setIsWatchingAmp(true);
      }
    } catch (e) {
      console.error('Error toggling amp watch:', e);
    }
    setWatchLoading(false);
  };

  const toggleWishlist = async () => {
    if (!isAuthenticated || wishlistLoading) return;
    setWishlistLoading(true);
    try {
      if (isWishlisted && wishlistCollectionId) {
        const resp = await fetch(`${API_URL}/collections/${wishlistCollectionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          setIsWishlisted(false);
          setWishlistCollectionId(null);
        }
      } else {
        const resp = await fetch(`${API_URL}/collections/wishlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ amplifier_id: parseInt(resolvedAmplifierId) })
        });
        if (resp.ok) {
          const data = await resp.json();
          setIsWishlisted(true);
          setWishlistCollectionId(data.collection_id);
        }
      }
    } catch (e) { /* silent */ }
    setWishlistLoading(false);
  };

  // Helper to build SEO-friendly amplifier URL
  const getAmpUrl = (amp) => {
    if (amp?.manufacturer_slug && amp?.slug) return `/amps/${amp.manufacturer_slug}/${amp.slug}`;
    return `/amplifier-components/${amp?.amplifier_id}`;
  };

  // Unique key for scroll restoration - works with both slug and ID routes
  const pageKey = amplifierId || `${manufacturerSlug}/${ampSlug}`;

  // Handle scroll restoration
  useEffect(() => {
    // Always scroll to top when loading an amp details page
    // (amp links now open in new tabs, so no need for scroll position restoration)
    window.scrollTo(0, 0);
  }, [pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAmpFetchError(null);
    const fetchAmplifier = async (retrying = false) => {
      try {
        let ampRes;
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        if (manufacturerSlug && ampSlug) {
          // Client-side doubled-slug guard: if the slug is "foo-foo" style (a
          // stale URL left over from before the 20260530 migration), navigate
          // to the canonical half immediately without making an API call.
          // This prevents Cloudflare from dropping the XHR for bot-crawled
          // stale URLs and logging a spurious "Network Error" in the error log.
          const mid = Math.floor(ampSlug.length / 2);
          if (ampSlug.length % 2 === 1 && ampSlug[mid] === '-') {
            const half = ampSlug.slice(0, mid);
            if (half.length > 0 && half === ampSlug.slice(mid + 1)) {
              navigate(`/amps/${manufacturerSlug}/${half}`, { replace: true });
              return;
            }
          }
          // SEO slug-based route
          ampRes = await ampApi.get(`/amplifiers/by-slug/${manufacturerSlug}/${ampSlug}`, { headers });
        } else if (amplifierId) {
          // Legacy ID-based route - include auth so owners can view their private amps
          ampRes = await ampApi.get(`/amplifiers/${amplifierId}`, { headers });
        } else {
          return;
        }
        setAmplifier(ampRes.data);
        setResolvedAmplifierId(ampRes.data.amplifier_id);
        // Normalize browser URL if we arrived via a legacy doubled slug
        // (e.g. /amps/swr/california-blonde-california-blonde → /amps/swr/california-blonde)
        if (
          ampSlug && ampRes.data.manufacturer_slug && ampRes.data.slug &&
          (ampSlug !== ampRes.data.slug || manufacturerSlug !== ampRes.data.manufacturer_slug)
        ) {
          navigate(`/amps/${ampRes.data.manufacturer_slug}/${ampRes.data.slug}`, { replace: true });
        }
      } catch (error) {
        console.error('Error fetching amplifier:', error);
        const status = error?.response?.status;
        // Auto-retry once on 503 (API restarting / deploy in progress)
        if (status === 503 && !retrying) {
          setTimeout(() => fetchAmplifier(true), 3000);
          return;
        }
        const message = error?.response?.data?.error || error?.message || 'Failed to load amplifier';
        setAmpFetchError({ status, message });
        // 403 = Cloudflare bot-blocking. 404 = amp doesn't exist (bots constructing bad URLs).
        // 429 = user hit the anonymous rate limit — expected, not an app bug.
        // !status (Network Error) = Cloudflare dropped the TCP connection instead of returning 403.
        // None of these are actionable application errors. Authenticated ID-route
        // 404s are recorded by the API with validated session identity.
        if (status === 403 || status === 404 || status === 429 || !status) return;
        // Log to server so the error appears in the admin Error Log screen
        try {
          fetch(`${API_URL}/errors/log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              severity: status === 404 ? 'warning' : 'error',
              source: 'frontend',
              message: `AmplifierDetailsScreen fetch failed (${status ?? 'network'}): ${message}`,
              stack_trace: error?.stack,
              error_code: error?.name || 'FetchError',
              page_url: window.location.href,
              metadata: {
                manufacturerSlug,
                ampSlug,
                amplifierId,
                status,
                retrying,
                timestamp: new Date().toISOString(),
              },
            }),
          });
        } catch (_) { /* never suppress the original error */ }
      }
    };
    fetchAmplifier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amplifierId, manufacturerSlug, ampSlug, ampFetchRetryKey]); // token intentionally excluded — slug route is public; no redirect needed

  // Update page title, meta tags, and canonical URL for SEO when amplifier data loads
  useEffect(() => {
    if (amplifier) {
      const ampTitle = `${amplifier.manufacturer_name || ''} ${amplifier.name || amplifier.model || ''} - AmpNebula`.trim();
      document.title = ampTitle;
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        const desc = amplifier.description 
          ? amplifier.description.substring(0, 160) 
          : `View detailed specs, photos, and community discussions for the ${amplifier.manufacturer_name || ''} ${amplifier.name || amplifier.model || ''} amplifier on AmpNebula.`;
        metaDesc.setAttribute('content', desc);
      }
      
      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', ampTitle);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) {
        const desc = amplifier.description 
          ? amplifier.description.substring(0, 200)
          : `${amplifier.manufacturer_name || ''} ${amplifier.name || amplifier.model || ''} amplifier details and community discussion.`;
        ogDesc.setAttribute('content', desc);
      }

      // Set canonical URL — always points to the clean slug URL, even if the user
      // arrived via the old /amplifier-components/:id path. This tells Google which
      // URL is authoritative and prevents duplicate-content penalties.
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink && amplifier.manufacturer_slug && amplifier.slug) {
        canonicalLink.setAttribute('href', `https://www.ampnebula.com/amps/${amplifier.manufacturer_slug}/${amplifier.slug}`);
      }
    }
    return () => {
      document.title = 'AmpNebula';
      // Reset canonical to site root when leaving amp page
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) canonicalLink.setAttribute('href', 'https://www.ampnebula.com');
    };
  }, [amplifier]);

  useEffect(() => {
    const fetchChildren = async () => {
      if (resolvedAmplifierId && amplifier) {
        try {
          const childRes = await ampApi.get(`/amplifiers/${resolvedAmplifierId}/children`);
          setChildren(childRes.data);
        } catch (error) {
          console.error('Error fetching child amplifiers:', error);
          setChildren([]);
        }
      } else {
        setChildren([]);
      }
    };
    fetchChildren();
  }, [resolvedAmplifierId, amplifier]);

  useEffect(() => {
    if (resolvedAmplifierId && amplifier) {
      artifactApi.get('/amplifier_artifacts', { params: { amplifier_id: resolvedAmplifierId } })
        .then(res => setArtifacts(Array.isArray(res.data) ? res.data : []))
        .catch(err => console.error('Error fetching artifacts:', err));
    } else {
      setArtifacts([]);
    }
  }, [resolvedAmplifierId, amplifier]);

  // Handle ?image= query parameter to open image viewer directly
  useEffect(() => {
    if (!imageParamHandled && artifacts.length > 0) {
      const imageParam = searchParams.get('image');
      if (imageParam !== null) {
        const imageIndex = parseInt(imageParam, 10);
        if (!isNaN(imageIndex) && imageIndex >= 0 && imageIndex < artifacts.length) {
          setViewerIndex(imageIndex);
          setViewerOpen(true);
        }
        setImageParamHandled(true);
      }
    }
  }, [artifacts, searchParams, imageParamHandled]);

  // Handle ?review_parts=1 query parameter to scroll to parts list
  useEffect(() => {
    if (reviewPartsParam && partsListRef.current && resolvedAmplifierId) {
      setTimeout(() => {
        partsListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      // Clear the param so re-navigation works
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('review_parts');
      setSearchParams(newParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewPartsParam, resolvedAmplifierId]);

  // Helper function to format year range display
  const formatYearRange = (amplifier) => {
    if (!amplifier) return 'N/A';
    
    const { start_year, end_year } = amplifier;
    
    if (start_year && end_year) {
      return start_year === end_year ? 
        start_year.toString() : 
        `${start_year} - ${end_year}`;
    } else if (start_year) {
      return `${start_year} - present`;
    } else if (end_year) {
      return `Unknown - ${end_year}`;
    }
    
    return 'N/A';
  };

  // Handle Artifact upload — accepts a FileList (from input onChange or drag-drop)
  const uploadFileList = async (files) => {
    if (readOnlyMode) return;
    if (!resolvedAmplifierId || !files || !files.length) return;
    setUploading(true);
    let blockedFiles = [];
    let failedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append('amplifier_id', resolvedAmplifierId);
      formData.append('artifact_type', uploadArtifactType);
      formData.append('artifact', file);
      try {
        await artifactApi.post('/amplifier_artifacts', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        if (err.response?.data?.code === 'IMAGE_MODERATION_BLOCKED') {
          blockedFiles.push(file.name);
        } else {
          console.error('Artifact upload error:', err);
          const msg = err.response?.data?.error || err.message || 'Unknown error';
          failedFiles.push(`${file.name} (${msg})`);
        }
      }
    }
    // Refresh artifact list
    const res = await artifactApi.get('/amplifier_artifacts', { params: { amplifier_id: resolvedAmplifierId } });
    setArtifacts(Array.isArray(res.data) ? res.data : []);
    setUploading(false);
    if (blockedFiles.length > 0) {
      alert(`The following file(s) were blocked for inappropriate content and could not be uploaded:\n${blockedFiles.join('\n')}`);
    }
    if (failedFiles.length > 0) {
      alert(`The following file(s) failed to upload:\n${failedFiles.join('\n')}`);
    }
  };

  const handleArtifactUpload = (e) => {
    uploadFileList(e.target.files);
    e.target.value = '';
  };

  const handleArtifactDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setArtifactDragOver(false);
    if (readOnlyMode || !resolvedAmplifierId || uploading) return;
    if (uploadArtifactType === 'youtube') return; // no drag-drop for URL-only type
    const files = e.dataTransfer.files;
    if (files && files.length > 0) uploadFileList(files);
  };

  // Approve a pending-review amp (admin only)
  const handleDeletePendingAmp = async () => {
    if (deletingAmp || !resolvedAmplifierId) return;
    if (!window.confirm(`Delete this amp permanently? This cannot be undone.`)) return;
    setDeletingAmp(true);
    try {
      await axios.delete(`/amplifiers/${resolvedAmplifierId}`);
      navigate(`/amps/${manufacturerSlug}`);
    } catch (err) {
      alert('Failed to delete amp. Please try again.');
      console.error(err);
    } finally {
      setDeletingAmp(false);
    }
  };

  const handleApprove = async () => {
    if (approvingAmp || !resolvedAmplifierId) return;
    setApprovingAmp(true);
    try {
      await axios.patch(`/amplifiers/${resolvedAmplifierId}/approve`);
      refreshAmplifier();
    } catch (err) {
      console.error('Failed to approve amp:', err);
      alert('Failed to approve amp. Please try again.');
    } finally {
      setApprovingAmp(false);
    }
  };

  // Handle YouTube video URL submission
  const handleYouTubeAdd = async () => {
    if (!youtubeUrl.trim() || !resolvedAmplifierId || uploading) return;
    const videoId = extractYouTubeId(youtubeUrl.trim());
    if (!videoId) {
      alert('Invalid YouTube URL. Use a youtube.com/watch?v=… or youtu.be/… link.');
      return;
    }
    setUploading(true);
    try {
      await artifactApi.post('/amplifier_artifacts/youtube',
        { amplifier_id: resolvedAmplifierId, external_url: youtubeUrl.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const res = await artifactApi.get('/amplifier_artifacts', { params: { amplifier_id: resolvedAmplifierId } });
      setArtifacts(Array.isArray(res.data) ? res.data : []);
      setYoutubeUrl('');
    } catch (err) {
      console.error('YouTube add error:', err);
      alert(err.response?.data?.error || 'Failed to add YouTube video');
    } finally {
      setUploading(false);
    }
  };

  // Handle Artifact type change (re-categorize existing artifact)
  const handleArtifactTypeChange = async (artifact_id, newType) => {
    try {
      await artifactApi.patch(`/amplifier_artifacts/${artifact_id}/type`, { artifact_type: newType }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = await artifactApi.get('/amplifier_artifacts', { params: { amplifier_id: resolvedAmplifierId } });
      setArtifacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error updating artifact type:', err);
    }
  };

  // Handle Artifact delete
  const handleArtifactDelete = async (artifact_id) => {
    if (readOnlyMode) return;
    await artifactApi.delete(`/amplifier_artifacts/${artifact_id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const res = await artifactApi.get('/amplifier_artifacts', { params: { amplifier_id: resolvedAmplifierId } });
    setArtifacts(Array.isArray(res.data) ? res.data : []);
    // If the deleted artifact was the pinned image, clear it locally
    if (amplifier?.pinned_artifact_id === artifact_id) {
      setAmplifier(prev => ({ ...prev, pinned_artifact_id: null }));
    }
  };

  // Handle pin/unpin artifact as hero image
  const handlePinArtifact = async (artifact_id) => {
    if (readOnlyMode) return;
    const newPinId = amplifier?.pinned_artifact_id === artifact_id ? null : artifact_id;
    const currentToken = token || safeStorage.getItem('authToken');
    if (!currentToken) {
      alert('You must be logged in to pin images');
      return;
    }
    const headers = { 'Authorization': `Bearer ${currentToken}` };
    try {
      const res = await ampApi.patch(`/amplifiers/${resolvedAmplifierId}/pin-image`, { artifact_id: newPinId }, { headers });
      setAmplifier(prev => ({ ...prev, pinned_artifact_id: res.data.pinned_artifact_id }));
    } catch (err) {
      console.error('Error pinning artifact:', err);
      alert(err.response?.data?.error || 'Failed to pin image');
    }
  };

  // Artifact viewer handlers — viewer always uses full artifacts array
  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };
  const closeViewer = () => setViewerOpen(false);
  const prevViewer = () => setViewerIndex(i => Math.max(i - 1, 0));
  const nextViewer = () => setViewerIndex(i => Math.min(i + 1, artifacts.length - 1));

  // Artifact type labels and icons
  const ARTIFACT_TYPE_CONFIG = {
    photo: { label: 'Photos', icon: '📷' },
    schematic: { label: 'Schematics', icon: '📐' },
    wiring_diagram: { label: 'Wiring', icon: '🔌' },
    layout: { label: 'Layouts', icon: '📏' },
    parts_list: { label: 'Parts', icon: '📋' },
    manual: { label: 'Manuals', icon: '📖' },
    voltage_chart: { label: 'Voltage Charts', icon: '⚡' },
    youtube: { label: 'Videos', icon: '▶️' },
    audio: { label: 'Audio', icon: '🎵' },
    other: { label: 'Other', icon: '📁' }
  };

  const SPREADSHEET_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/vnd.oasis.opendocument.spreadsheet',
  ];
  const isSpreadsheet = (mimeType) => SPREADSHEET_MIME_TYPES.includes(mimeType);

  // Get filtered artifacts for current tab
  const getFilteredArtifacts = () => {
    if (artifactTab === 'all') return artifacts;
    return artifacts.filter(a => a.artifact_type === artifactTab);
  };

  // Get counts per type for tab badges
  const getArtifactTypeCounts = () => {
    const counts = {};
    artifacts.forEach(a => {
      const type = a.artifact_type || 'photo';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  };

  // Map filtered artifact index to full artifacts array index (for viewer)
  const getGlobalIndex = (filteredIndex) => {
    if (artifactTab === 'all') return filteredIndex;
    const filtered = getFilteredArtifacts();
    const artifact = filtered[filteredIndex];
    return artifacts.findIndex(a => a.artifact_id === artifact.artifact_id);
  };

    // Child amplifier management functions
  const refreshChildren = async () => {
    if (resolvedAmplifierId) {
      try {
        const childRes = await ampApi.get(`/amplifiers/${resolvedAmplifierId}/children`);
        setChildren(childRes.data);
      } catch (error) {
        console.error('Error fetching child amplifiers:', error);
        setChildren([]);
      }
    }
  };

  const handleCreateChild = () => {
    if (readOnlyMode) return;
    // Navigate to the main amplifier screen with parent_id to create a child
    navigate(`/amplifier_screen?parent_id=${resolvedAmplifierId}`);
  };

  const handleDeleteChild = async (childId) => {
    if (readOnlyMode) return;
    if (window.confirm('Are you sure you want to delete this individual amplifier unit?')) {
      try {
        await ampApi.delete(`/amplifiers/${childId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        await refreshChildren();
      } catch (error) {
        console.error('Error deleting child amplifier:', error);
        alert('Error deleting child amplifier. Please try again.');
      }
    }
  };

  // Visual editor functions (similar to AmplifierForm)
  const getHtmlFromEditorState = (state) => {
    const rawContentState = convertToRaw(state.getCurrentContent());
    return draftToHtml(rawContentState);
  };

  const setEditorStateFromHtml = (html) => {
    const blocksFromHtml = htmlToDraft(html);
    if (blocksFromHtml) {
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  const handleTabSwitch = (tab) => {
    if (tab === 'html' && activeTab === 'visual') {
      setHtmlContent(getHtmlFromEditorState(editorState));
      setHtmlDirty(false);
    } else if (tab === 'visual' && activeTab === 'html') {
      if (htmlDirty && !useRawHtmlMode) {
        const confirmed = window.confirm(
          'Warning: Switching to Visual mode will convert your HTML to a simplified format.\n\n' +
          'Complex formatting (tables, custom styles, divs, etc.) may be lost or altered.\n\n' +
          'To preserve your exact HTML, check "Use Raw HTML Mode" checkbox instead.\n\n' +
          'Continue switching to Visual mode?'
        );
        if (!confirmed) {
          return;
        }
      }
      setEditorState(setEditorStateFromHtml(htmlContent));
    }
    setActiveTab(tab);
  };

  const handleHtmlChange = (e) => {
    setHtmlContent(e.target.value);
    setHtmlDirty(true);
  };

  const toggleBold = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'BOLD');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  const toggleItalic = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'ITALIC');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  const toggleUnderline = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'UNDERLINE');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  const applyFontSize = (size) => {
    const selection = editorState.getSelection();
    
    if (selection.isCollapsed()) {
      const currentStyle = editorState.getCurrentInlineStyle();
      const sizeStyles = currentStyle.filter(style => style.startsWith('FONT_SIZE_'));
      let newEditorState = editorState;
      
      sizeStyles.forEach(style => {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, style);
      });
      
      newEditorState = RichUtils.toggleInlineStyle(newEditorState, `FONT_SIZE_${size}`);
      setEditorState(newEditorState);
    } else {
      let contentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();
      
      const sizeStyles = editorState.getCurrentInlineStyle().filter(style => style.startsWith('FONT_SIZE_'));
      sizeStyles.forEach(style => {
        contentState = RichUtils.toggleInlineStyle(EditorState.createWithContent(contentState), style).getCurrentContent();
      });
      
      contentState = RichUtils.toggleInlineStyle(
        EditorState.createWithContent(contentState),
        `FONT_SIZE_${size}`
      ).getCurrentContent();
      
      const newEditorState = EditorState.push(editorState, contentState, 'change-inline-style');
      const finalEditorState = EditorState.forceSelection(newEditorState, selectionState);
      setEditorState(finalEditorState);
    }
  };

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  // Handle pasted text - insert as plain text to avoid formatting issues
  const handlePastedText = (text, html, editorStateParam) => {
    // Insert the pasted text at the current cursor position
    const currentContent = editorStateParam.getCurrentContent();
    const selection = editorStateParam.getSelection();
    
    // Use Modifier to insert the text
    const newContent = Modifier.replaceText(
      currentContent,
      selection,
      text
    );
    
    const newEditorState = EditorState.push(
      editorStateParam,
      newContent,
      'insert-characters'
    );
    
    setEditorState(newEditorState);
    return 'handled';
  };

  const styleMap = {};
  for (let i = 10; i <= 24; i++) {
    styleMap[`FONT_SIZE_${i}`] = {
      fontSize: `${i}px`,
    };
  }

  // Inline editing handlers for Amplifier Information section
  const startEditingInfo = () => {
    if (readOnlyMode) return;
    setInfoFormData({
      name: amplifier?.name || '',
      model: amplifier?.model || '',
      serial_number: amplifier?.serial_number || '',
      start_year: amplifier?.start_year || '',
      end_year: amplifier?.end_year || ''
    });
    setEditingInfo(true);
  };

  const cancelEditingInfo = () => {
    setEditingInfo(false);
    setInfoFormData({});
  };

  const saveInfoChanges = async () => {
    if (readOnlyMode || !resolvedAmplifierId) return;
    setSavingInfo(true);
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      await ampApi.put(`/amplifiers/${resolvedAmplifierId}`, {
        manufacturer_id: amplifier.manufacturer_id,
        name: infoFormData.name || null,
        model: infoFormData.model || null,
        serial_number: infoFormData.serial_number || null,
        start_year: infoFormData.start_year || null,
        end_year: infoFormData.end_year || null,
        description: amplifier.description,
        template_id: amplifier.template_id,
        is_parent: amplifier.is_parent,
        parent_id: amplifier.parent_id
      }, { headers });
      // Refresh amplifier data
      const ampRes = await ampApi.get(`/amplifiers/${resolvedAmplifierId}`, { headers });
      setAmplifier(ampRes.data);
      setEditingInfo(false);
    } catch (error) {
      console.error('Error saving amplifier info:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSavingInfo(false);
    }
  };

  // Refresh amplifier data (used by wiki suggest edit after auto-approve)
  const refreshAmplifier = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const ampRes = await ampApi.get(`/amplifiers/${resolvedAmplifierId}`, { headers });
      setAmplifier(ampRes.data);
    } catch (error) {
      console.error('Error refreshing amplifier:', error);
    }
  };

  // Fetch wiki sections (community-contributed content beyond the description)
  const fetchWikiSections = async () => {
    if (!resolvedAmplifierId) return;
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const resp = await fetch(`${API_URL}/wiki/${resolvedAmplifierId}/sections`, { headers });
      if (!resp.ok) { resp.body?.cancel(); return; }
      {
        const data = await resp.json();
        // Build a map of section_type → section data (exclude 'description' — handled separately)
        const map = {};
        (data.sections || []).forEach(s => {
          if (s.section_type !== 'description' && s.content) {
            map[s.section_type] = s;
          }
        });
        setWikiSections(map);
      }
    } catch (e) { /* silent */ }
  };

  const fetchAmpHistory = async () => {
    if (!resolvedAmplifierId) return;
    setAmpHistoryLoading(true);
    try {
      const resp = await fetch(`${API_URL}/wiki/${resolvedAmplifierId}/history`);
      if (resp.ok) {
        const data = await resp.json();
        setAmpHistory(data.entries || []);
      }
    } catch (e) { /* silent */ }
    setAmpHistoryLoading(false);
  };

  // Fetch wiki sections when amp loads
  useEffect(() => {
    if (resolvedAmplifierId && amplifier) {
      fetchWikiSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAmplifierId, amplifier]);

  // Inline editing handlers for Description section
  const startEditingDescription = () => {
    if (readOnlyMode) return;
    
    // Initialize the editor based on the current description format
    if (amplifier?.description) {
      if (amplifier.description.startsWith('__RAW_HTML__')) {
        const rawHtml = amplifier.description.replace('__RAW_HTML__', '');
        setHtmlContent(rawHtml);
        setUseRawHtmlMode(true);
        setActiveTab('html');
        setEditorState(setEditorStateFromHtml(rawHtml));
      } else {
        try {
          const parsedDescription = JSON.parse(amplifier.description);
          setEditorState(EditorState.createWithContent(convertFromRaw(parsedDescription)));
          setHtmlContent(getHtmlFromEditorState(EditorState.createWithContent(convertFromRaw(parsedDescription))));
          setUseRawHtmlMode(false);
          setActiveTab('visual');
        } catch {
          const rawHtml = amplifier.description || '';
          setHtmlContent(rawHtml);
          setUseRawHtmlMode(true);
          setActiveTab('html');
          setEditorState(setEditorStateFromHtml(rawHtml));
        }
      }
    } else {
      setEditorState(EditorState.createEmpty());
      setHtmlContent('');
      setUseRawHtmlMode(false);
      setActiveTab('visual');
    }
    
    setEditingDescription(true);
    setHtmlDirty(false);
  };

  const cancelEditingDescription = () => {
    setEditingDescription(false);

    setEditorState(EditorState.createEmpty());
    setHtmlContent('');
    setActiveTab('visual');
    setUseRawHtmlMode(false);
    setHtmlDirty(false);
  };

  const saveDescriptionChanges = async () => {
    if (readOnlyMode || !resolvedAmplifierId) return;
    setSavingDescription(true);
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      let descriptionToSave;
      
      if (useRawHtmlMode || activeTab === 'html') {
        // Save as raw HTML
        descriptionToSave = htmlContent ? `__RAW_HTML__${htmlContent}` : null;
      } else {
        // Save as Draft.js JSON
        const rawContentState = convertToRaw(editorState.getCurrentContent());
        descriptionToSave = JSON.stringify(rawContentState);
      }
      
      await ampApi.put(`/amplifiers/${resolvedAmplifierId}`, {
        manufacturer_id: amplifier.manufacturer_id,
        name: amplifier.name,
        model: amplifier.model,
        serial_number: amplifier.serial_number,
        start_year: amplifier.start_year,
        end_year: amplifier.end_year,
        description: descriptionToSave,
        template_id: amplifier.template_id,
        is_parent: amplifier.is_parent,
        parent_id: amplifier.parent_id
      }, { headers });
      
      // Refresh amplifier data
      const ampRes = await ampApi.get(`/amplifiers/${resolvedAmplifierId}`, { headers });
      setAmplifier(ampRes.data);
      setEditingDescription(false);
    } catch (error) {
      console.error('Error saving description:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSavingDescription(false);
    }
  };

  // Generate description using Gemini AI
  const generateDescription = async () => {
    if (!amplifier?.manufacturer_name || !amplifier?.model) {
      alert('Amplifier must have a manufacturer and model to generate a description.');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const response = await ampApi.post('/generate-description', {
        make: amplifier.manufacturer_name,
        model: amplifier.model,
        startYear: amplifier.start_year || null,
        endYear: amplifier.end_year || null,
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      if (response.data?.html) {
        // Set the generated HTML content
        setHtmlContent(response.data.html);
        setUseRawHtmlMode(true);
        setActiveTab('html');
        setEditorState(setEditorStateFromHtml(response.data.html));
        setHtmlDirty(true);
      }
    } catch (error) {
      console.error('Error generating description:', error);
      const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      const status = error.response?.status;
      if (status === 403) {
        alert('You do not have permission to generate descriptions.');
      } else if (status === 503) {
        alert('AI service is not available. Please try again later.');
      } else {
        alert(`Error generating description: ${message}`);
      }
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Convert Draft.js description to HTML (also handles raw HTML format)
  // Prepare wiki HTML for Wikipedia-style image layout:
  // 1. Hoist <img> out of <p> so CSS float works across paragraph text
  // 2. Add clear:right only to the 2nd+ image per heading section so images
  //    stack within a section but don't bleed into the next one (<h3> already
  //    has clear:both which resets floats between sections)
  const prepareWikiHtml = (html) => {
    if (!html) return html;

    let result = html;

    // Step 1: Hoist <img> tags out of <p> elements.
    // The negative lookahead (?!<\/p>) prevents the lazy [\s\S]*? from
    // crossing </p> boundaries — without it, the regex could span multiple
    // paragraphs and drag an image from a later section into an earlier one.
    if (result.includes('<img')) {
      const re = /(<p[^>]*>)((?:(?!<\/p>)[\s\S])*?)(<img\s[^>]*?>)((?:(?!<\/p>)[\s\S])*?)(<\/p>)/i;
      let prev;
      do {
        prev = result;
        result = result.replace(re, (_, pOpen, before, imgTag, after, pClose) => {
          const remaining = (before + after).trim();
          return imgTag + (remaining ? pOpen + remaining + pClose : '');
        });
      } while (result !== prev);
    }

    // Step 2: Add clear:right inline to the 2nd+ image within each
    // heading section. <h3> has clear:both in CSS, so the first image per
    // section floats at the correct vertical position without clearing
    // against the previous section's floats.
    const sections = result.split(/(?=<h3[\s>])/i);
    result = sections.map(section => {
      let imgCount = 0;
      return section.replace(/<img\s[^>]*?>/gi, (imgTag) => {
        imgCount++;
        if (imgCount <= 1) return imgTag;
        if (/style\s*=/i.test(imgTag)) {
          return imgTag.replace(/style\s*=\s*"([^"]*)"/i, 'style="clear:right; $1"');
        }
        return imgTag.replace(/<img\s/i, '<img style="clear:right" ');
      });
    }).join('');

    return result;
  };

  const getDescriptionHTML = (description) => {
    if (!description) return '';
    
    // Check if it's raw HTML (has our marker prefix)
    if (description.startsWith('__RAW_HTML__')) {
      return prepareWikiHtml(description.replace('__RAW_HTML__', ''));
    }
    
    try {
      const rawContentState = JSON.parse(description);
      return prepareWikiHtml(draftToHtml(rawContentState));
    } catch (error) {
      // If parsing fails, return the description as plain text
      return description;
    }
  };

  // Hero artifact for read-only display: use pinned image, or fall back to first available photo
  const heroArtifactId = amplifier?.pinned_artifact_id ||
    (Array.isArray(artifacts) ? artifacts.find(a => a.mime_type?.startsWith('image/'))?.artifact_id ?? null : null);

  // Early return for 404 — show a helpful not-found page instead of a blank shell
  if (ampFetchError?.status === 404) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', borderRadius: '10px', padding: '3rem 2rem', border: '1px solid #e9ecef', maxWidth: '480px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>Amplifier Not Found</h1>
          <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            The amplifier at this URL doesn't exist in our catalog.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {manufacturerSlug && (
              <a
                href={`/amps/${manufacturerSlug}`}
                style={{ display: 'block', padding: '0.6rem 1.2rem', background: '#2563eb', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '500', fontSize: '0.95rem' }}
              >
                Browse {manufacturerSlug.charAt(0).toUpperCase() + manufacturerSlug.slice(1)} amps
              </a>
            )}
            <a
              href="/amps"
              style={{ display: 'block', padding: '0.6rem 1.2rem', background: '#f1f5f9', color: '#333', borderRadius: '8px', textDecoration: 'none', fontWeight: '500', fontSize: '0.95rem', border: '1px solid #e2e8f0' }}
            >
              Browse all amps
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-outer" style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2rem'
    }}>
      <UploadOverlay
        visible={uploading}
        message="Uploading file…"
        subtext="Processing your upload"
      />
      <div className="ads-inner" style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {isAdmin && amplifier && amplifier.pending_review && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '10px',
            padding: '1rem 1.5rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div>
              <strong style={{ color: '#856404' }}>⏳ Pending Review</strong>
              <span style={{ color: '#856404', marginLeft: '0.75rem', fontSize: '0.9rem' }}>
                This amp is not yet visible to the public. Review the description and photos, then approve when ready.
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleApprove}
                disabled={approvingAmp || deletingAmp}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  cursor: (approvingAmp || deletingAmp) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  opacity: approvingAmp ? 0.7 : 1
                }}
              >
                {approvingAmp ? 'Approving…' : '✓ Approve'}
              </button>
              <button
                onClick={handleDeletePendingAmp}
                disabled={deletingAmp || approvingAmp}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  cursor: (deletingAmp || approvingAmp) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  opacity: deletingAmp ? 0.7 : 1
                }}
              >
                {deletingAmp ? 'Deleting…' : '✕ Delete'}
              </button>
            </div>
          </div>
        )}
        <div className="ads-title-card" style={{
          background: 'white',
          borderRadius: '10px',
          padding: '3rem',
          border: '1px solid #e9ecef',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <h1 className="ads-page-h1" style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            color: '#333',
            margin: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            Amplifier Details
            {amplifier && !amplifier.is_parent && (
              <span style={{ 
                fontSize: '0.4em', 
                background: '#2563eb',
                color: 'white', 
                padding: '0.5rem 1rem', 
                borderRadius: '10px',
                fontWeight: '600',
                
              }}>
                Individual Unit
              </span>
            )}
            {amplifier && amplifier.is_parent && (
              <span style={{ 
                fontSize: '0.4em', 
                background: '#1d4ed8',
                color: 'white', 
                padding: '0.5rem 1rem', 
                borderRadius: '10px',
                fontWeight: '600',
                
              }}>
                Parent Specification
              </span>
            )}
          </h1>
          {amplifier && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <SocialShareBar
                url={getAmpUrl(amplifier)}
                title={`${amplifier.manufacturer_name || ''} ${amplifier.name || 'Amplifier'} - AmpNebula`}
              />
              {isAuthenticated && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={toggleWishlist}
                    disabled={wishlistLoading || !!ownedChildId}
                    title={ownedChildId ? 'You own this amp' : isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.45rem 0.9rem',
                      borderRadius: '8px',
                      border: isWishlisted ? '1px solid rgba(248, 81, 73, 0.4)' : '1px solid #ddd',
                      background: isWishlisted ? 'rgba(248, 81, 73, 0.15)' : 'white',
                      color: isWishlisted ? '#ff6b6b' : '#555',
                      fontSize: '0.82rem',
                      fontWeight: '500',
                      cursor: (wishlistLoading || ownedChildId) ? 'default' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: (wishlistLoading || ownedChildId) ? 0.5 : 1
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" 
                      fill={(isWishlisted || ownedChildId) ? 'currentColor' : 'none'} 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    {ownedChildId ? 'Owned' : isWishlisted ? 'Wishlisted' : 'Wishlist'}
                  </button>
                  <button
                    onClick={toggleWatchAmp}
                    disabled={watchLoading}
                    title={isWatchingAmp ? 'Stop watching this amplifier' : 'Watch this amplifier to get notified of changes'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.45rem 0.9rem',
                      borderRadius: '8px',
                      border: isWatchingAmp ? '1px solid rgba(72, 187, 120, 0.4)' : '1px solid #ddd',
                      background: isWatchingAmp ? 'rgba(72, 187, 120, 0.15)' : 'white',
                      color: isWatchingAmp ? '#38a169' : '#555',
                      fontSize: '0.82rem',
                      fontWeight: '500',
                      cursor: watchLoading ? 'wait' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: watchLoading ? 0.5 : 1
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={isWatchingAmp ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    {isWatchingAmp ? 'Watching' : 'Watch'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Amplifier Information */}
        <div className="ads-section-card" style={{
          background: 'white',
          borderRadius: '10px',
          padding: '2rem',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          marginBottom: '2rem'
        }}>
          <>
            {/* Visual header: hero image, title, S/N badge — shown for all users */}
            <div className="ads-hero-section" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              justifyContent: heroArtifactId ? 'flex-start' : 'center'
            }}>
              {/* Hero image: pinned artifact or first available photo */}
              {heroArtifactId && (
                <div className="ads-hero-image-wrap" style={{
                  flexShrink: 0,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                  border: '2px solid #e9ecef',
                  cursor: 'pointer'
                }}
                  onClick={() => {
                    const idx = artifacts.findIndex(a => a.artifact_id === heroArtifactId);
                    if (idx >= 0) openViewer(idx);
                  }}
                >
                  <img
                    className="ads-hero-img"
                    src={`${API_URL}/amplifier_artifacts/${heroArtifactId}/thumbnail`}
                    alt="Amplifier"
                    style={{ maxWidth: 320, maxHeight: 280, objectFit: 'contain', display: 'block' }}
                  />
                </div>
              )}
              <div style={{ textAlign: heroArtifactId ? 'left' : 'center', flex: 1, minWidth: 200 }}>
              <div className="ads-amp-title" style={{
                color: '#333',
                fontSize: '2rem',
                fontWeight: '700',
                marginBottom: '1rem',
                lineHeight: '1.2',
                
              }}>
                {(() => {
                  const parts = [];
                  const mfr = amplifier?.manufacturer_name || '';
                  
                  // Add year range if available
                  if (amplifier?.start_year || amplifier?.end_year) {
                    parts.push(formatYearRange(amplifier));
                  }
                  
                  // Add manufacturer
                  if (mfr) {
                    parts.push(mfr);
                  }
                  
                  // Add name, stripping leading manufacturer prefix to avoid duplication
                  // e.g. name="Marshall JTM 100" with mfr="Marshall" shows just "JTM 100"
                  if (amplifier?.name) {
                    const displayName = (mfr && amplifier.name.startsWith(mfr + ' '))
                      ? amplifier.name.slice(mfr.length + 1)
                      : amplifier.name;
                    parts.push(displayName);
                  }
                  
                  return parts.join(' ') || 'Unnamed Amplifier';
                })()}
              </div>
              
              {/* Serial Number as a badge if present */}
              {amplifier?.serial_number && (
                <div style={{
                  display: 'inline-block',
                  background: '#dc3545',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  
                }}>
                  S/N: {amplifier.serial_number}
                </div>
              )}
              
              {/* Model as subtitle if different from name */}
              {amplifier?.model && amplifier.model !== amplifier.name && (
                <div style={{
                  color: '#666',
                  fontSize: '1.4rem',
                  fontStyle: 'italic',
                  marginTop: '0.5rem'
                }}>
                  Model: {amplifier.model}
                </div>
              )}
              </div>
            </div>
            <>
              <div style={{
                marginTop: '2rem',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid #2563eb'
              }}>
                <h2 style={{
                  margin: '0',
                  color: '#333',
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Amplifier Information
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {!readOnlyMode && !editingInfo && (
                      <button
                        onClick={startEditingInfo}
                        style={{
                          background: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </h2>
              </div>
              
              {ampFetchError ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <p style={{ color: '#dc2626', fontSize: '1rem', marginBottom: '0.75rem' }}>
                    {ampFetchError.status === 404
                      ? 'Amplifier not found.'
                      : ampFetchError.status === 429
                      ? "You've been browsing quickly — please wait a moment, then refresh the page."
                      : `Failed to load amplifier${ampFetchError.status ? ` (${ampFetchError.status})` : ''}. Please try again.`}
                  </p>
                  {ampFetchError.status !== 404 && ampFetchError.status !== 429 && (
                    <button
                      onClick={() => { setAmpFetchError(null); setAmplifier(null); setAmpFetchRetryKey(k => k + 1); }}
                      style={{ padding: '0.4rem 1.2rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : !amplifier ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#999', fontSize: '1rem' }}>
                  Loading amplifier data…
                </div>
              ) : !readOnlyMode && editingInfo ? (
                // Edit mode - show input fields
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '1.5rem'
                  }}>
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid #e9ecef'
                    }}>
                      <label style={{
                        display: 'block',
                        color: '#2563eb',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        Name
                      </label>
                      <input
                        type="text"
                        value={infoFormData.name || ''}
                        onChange={(e) => setInfoFormData({ ...infoFormData, name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          fontSize: '1.1rem',
                          fontWeight: '500',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          background: 'white',
                          boxSizing: 'border-box',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(37, 99, 235, 0.3)'}
                        placeholder="Enter amplifier name"
                      />
                    </div>
                    
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid #e9ecef'
                    }}>
                      <label style={{
                        display: 'block',
                        color: '#888',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        Manufacturer
                      </label>
                      <div style={{
                        color: '#333',
                        fontSize: '1.1rem',
                        fontWeight: '500',
                        padding: '0.75rem',
                        background: '#f0f0f0',
                        borderRadius: '10px',
                        border: '1px solid #e9ecef',
                        minHeight: '1.5rem',
                        fontStyle: 'italic'
                      }}>
                        {amplifier?.manufacturer_name || 'N/A'} (cannot be changed)
                      </div>
                    </div>
                    
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid #e9ecef'
                    }}>
                      <label style={{
                        display: 'block',
                        color: '#888',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        Model
                      </label>
                      <input
                        type="text"
                        value={infoFormData.model || ''}
                        onChange={(e) => setInfoFormData({ ...infoFormData, model: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          fontSize: '1.1rem',
                          fontWeight: '500',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          background: 'white',
                          boxSizing: 'border-box',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#00b894'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(0, 184, 148, 0.3)'}
                        placeholder="Enter model"
                      />
                    </div>
                    
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid #e9ecef'
                    }}>
                      <label style={{
                        display: 'block',
                        color: '#888',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        Year Range
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={infoFormData.start_year || ''}
                          onChange={(e) => setInfoFormData({ ...infoFormData, start_year: e.target.value })}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                          background: 'white',
                            boxSizing: 'border-box',
                            outline: 'none'
                          }}
                          placeholder="Start Year"
                        />
                        <span style={{ color: '#666', fontWeight: '600' }}>—</span>
                        <input
                          type="number"
                          value={infoFormData.end_year || ''}
                          onChange={(e) => setInfoFormData({ ...infoFormData, end_year: e.target.value })}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                          background: 'white',
                            boxSizing: 'border-box',
                            outline: 'none'
                          }}
                          placeholder="End Year"
                        />
                      </div>
                    </div>
                    
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid #e9ecef'
                    }}>
                      <label style={{
                        display: 'block',
                        color: '#888',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        Serial Number
                      </label>
                      <input
                        type="text"
                        value={infoFormData.serial_number || ''}
                        onChange={(e) => setInfoFormData({ ...infoFormData, serial_number: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          fontSize: '1.1rem',
                          fontWeight: '500',
                          border: '2px solid rgba(255, 46, 84, 0.3)',
                          borderRadius: '6px',
                          background: 'white',
                          boxSizing: 'border-box',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#ff2e54'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(255, 46, 84, 0.3)'}
                        placeholder="Enter serial number"
                      />
                    </div>
                  </div>
                  
                  {/* Save/Cancel buttons */}
                  <div style={{ 
                    marginTop: '1.5rem', 
                    display: 'flex', 
                    gap: '1rem', 
                    justifyContent: 'flex-end',
                    paddingTop: '1rem',
                    borderTop: '1px solid rgba(37, 99, 235, 0.2)'
                  }}>
                    <button
                      onClick={cancelEditingInfo}
                      disabled={savingInfo}
                      style={{
                        background: 'rgba(108, 117, 125, 0.1)',
                        color: '#6c757d',
                        border: '2px solid rgba(108, 117, 125, 0.3)',
                        borderRadius: '10px',
                        padding: '0.75rem 1.5rem',
                        cursor: savingInfo ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveInfoChanges}
                      disabled={savingInfo}
                      style={{
                        background: savingInfo 
                          ? 'rgba(37, 99, 235, 0.5)' 
                          : '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '0.75rem 1.5rem',
                        cursor: savingInfo ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {savingInfo ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </>
              ) : (
                // Display mode - show current values
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '1.5rem'
                }}>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#2563eb',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Name
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid rgba(37, 99, 235, 0.1)',
                      minHeight: '1.5rem'
                    }}>
                      {amplifier?.name || 'N/A'}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#888',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Manufacturer
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      minHeight: '1.5rem'
                    }}>
                      {amplifier?.manufacturer_name || 'N/A'}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#888',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Template
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      minHeight: '1.5rem'
                    }}>
                      {amplifier?.template_name || 'N/A'}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#888',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Model
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid rgba(0, 184, 148, 0.1)',
                      minHeight: '1.5rem'
                    }}>
                      {amplifier?.model || 'N/A'}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#888',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Year Range
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      minHeight: '1.5rem'
                    }}>
                      {formatYearRange(amplifier)}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <label style={{
                      display: 'block',
                      color: '#888',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      Serial Number
                    </label>
                    <div style={{
                      color: '#333',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '1px solid #e9ecef',
                      minHeight: '1.5rem'
                    }}>
                      {amplifier?.serial_number || 'N/A'}
                    </div>
                  </div>
                </div>
              )}
            </>
          </>
        </div>
        
        {/* Show parent relationship for child amplifiers */}
        {amplifier && !amplifier.is_parent && (
          <div style={{
            background: 'rgba(25, 118, 210, 0.1)',
            borderRadius: '10px',
            padding: '2rem',
            border: '1px solid rgba(25, 118, 210, 0.3)',
            marginBottom: '2rem'
          }}>
            <div style={{
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #1976d2'
            }}>
              <h2 style={{
                margin: '0',
                color: '#333',
            fontSize: '1.5rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                Individual Unit - Parent Specification
              </h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ 
                margin: '0', 
                fontSize: '1.1rem',
                color: '#333'
              }}>
                <strong>Parent:</strong> {amplifier.parent_name || 'Unknown'} 
                {amplifier.parent_model && ` (${amplifier.parent_model})`}
              </p>
            </div>
          </div>
        )}
        
        {/* Description Section */}
        <div className="ads-section-card" style={{
          background: 'white',
          borderRadius: '10px',
          padding: '2rem',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          marginBottom: '2rem'
        }}>
          <div style={{
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #2563eb'
          }}>
            <h2 style={{
              margin: '0',
              color: '#333',
              fontSize: '1.8rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Description
              </span>
              {!readOnlyMode && !editingDescription && (
                <button
                  onClick={startEditingDescription}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                  }}
                >
                  Edit
                </button>
              )}
            </h2>
          </div>
          
          {editingDescription ? (
            // Edit mode - show visual/HTML editor (similar to AmplifierForm)
            <>
              {/* Tab Navigation */}
              <div style={{ 
                display: 'flex', 
                borderBottom: '2px solid #ddd',
                marginBottom: '0'
              }}>
                <button
                  type="button"
                  onClick={() => handleTabSwitch('visual')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderBottom: activeTab === 'visual' ? '3px solid #2563eb' : '3px solid transparent',
                    backgroundColor: activeTab === 'visual' ? '#f8f9fa' : 'white',
                    color: activeTab === 'visual' ? '#2563eb' : '#666',
                    fontWeight: activeTab === 'visual' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-2px'
                  }}
                >
                  Visual
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSwitch('html')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderBottom: activeTab === 'html' ? '3px solid #2563eb' : '3px solid transparent',
                    backgroundColor: activeTab === 'html' ? '#f8f9fa' : 'white',
                    color: activeTab === 'html' ? '#2563eb' : '#666',
                    fontWeight: activeTab === 'html' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-2px'
                  }}
                >
                  &lt;/&gt; HTML
                </button>
              </div>
              
              {activeTab === 'visual' ? (
                <>
                  {/* Rich Text Toolbar */}
                  <div style={{ 
                    border: '1px solid #ccc', 
                    borderBottom: 'none', 
                    backgroundColor: '#f8f9fa', 
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {/* Bold Button */}
                    <button
                      type="button"
                      onClick={toggleBold}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        backgroundColor: editorState.getCurrentInlineStyle().has('BOLD') ? '#2563eb' : 'white',
                        color: editorState.getCurrentInlineStyle().has('BOLD') ? 'white' : 'black',
                        cursor: 'pointer',
                        borderRadius: '3px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                      title="Bold (Ctrl+B)"
                    >
                      B
                    </button>

                    {/* Italic Button */}
                    <button
                      type="button"
                      onClick={toggleItalic}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        backgroundColor: editorState.getCurrentInlineStyle().has('ITALIC') ? '#2563eb' : 'white',
                        color: editorState.getCurrentInlineStyle().has('ITALIC') ? 'white' : 'black',
                        cursor: 'pointer',
                        borderRadius: '3px',
                        fontStyle: 'italic',
                        transition: 'all 0.2s ease'
                      }}
                      title="Italic (Ctrl+I)"
                    >
                      I
                    </button>

                    {/* Underline Button */}
                    <button
                      type="button"
                      onClick={toggleUnderline}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        backgroundColor: editorState.getCurrentInlineStyle().has('UNDERLINE') ? '#2563eb' : 'white',
                        color: editorState.getCurrentInlineStyle().has('UNDERLINE') ? 'white' : 'black',
                        cursor: 'pointer',
                        borderRadius: '3px',
                        textDecoration: 'underline',
                        transition: 'all 0.2s ease'
                      }}
                      title="Underline (Ctrl+U)"
                    >
                      U
                    </button>

                    {/* Font Size Controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '12px', marginRight: '8px' }}>Font Size:</span>
                      <input
                        type="number"
                        value={fontSize}
                        onChange={(e) => {
                          const newSize = Math.min(Math.max(parseInt(e.target.value) || 10, 10), 24);
                          setFontSize(newSize);
                          applyFontSize(newSize);
                        }}
                        style={{
                          width: '60px',
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          textAlign: 'center',
                          fontSize: '12px'
                        }}
                        min="10"
                        max="24"
                        step="1"
                        title="Font Size in Pixels"
                      />
                      <span style={{ fontSize: '11px', color: '#666' }}>px</span>
                    </div>
                  </div>

                  {/* Visual Editor */}
                  <div style={{ 
                    border: '1px solid #ccc', 
                    minHeight: '200px', 
                    padding: '12px',
                    backgroundColor: 'white'
                  }}>
                    <Editor 
                      editorState={editorState} 
                      onChange={setEditorState}
                      customStyleMap={styleMap}
                      handleKeyCommand={handleKeyCommand}
                      handlePastedText={handlePastedText}
                      placeholder="Enter amplifier description..."
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* HTML Editor Header */}
                  <div style={{ 
                    border: '1px solid #ccc', 
                    borderBottom: 'none', 
                    backgroundColor: '#f8f9fa', 
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Tip: Edit the raw HTML below. Switch to Visual tab to see how it renders.
                    </div>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      backgroundColor: useRawHtmlMode ? '#d4edda' : '#fff3cd',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      border: useRawHtmlMode ? '1px solid #28a745' : '1px solid #ffc107'
                    }}>
                      <input
                        type="checkbox"
                        checked={useRawHtmlMode}
                        onChange={(e) => setUseRawHtmlMode(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: '500', color: useRawHtmlMode ? '#155724' : '#856404' }}>
                        {useRawHtmlMode ? '✓ Raw HTML Mode (preserves exact HTML)' : 'Use Raw HTML Mode'}
                      </span>
                    </label>
                  </div>
                  {useRawHtmlMode && (
                    <div style={{
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderTop: 'none',
                      padding: '8px 12px',
                      fontSize: '11px',
                      color: '#155724'
                    }}>
                      <strong>Raw HTML Mode enabled:</strong> Your HTML will be saved exactly as entered, preserving all formatting. 
                      The Visual tab may not display complex HTML correctly, but it will be stored and rendered properly.
                    </div>
                  )}
                  <textarea
                    value={htmlContent}
                    onChange={handleHtmlChange}
                    style={{
                      width: '100%',
                      minHeight: '200px',
                      padding: '12px',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      border: '1px solid #ccc',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      resize: 'vertical',
                      backgroundColor: '#2d2d2d',
                      color: '#f8f8f2',
                      boxSizing: 'border-box'
                    }}
                    placeholder="<p>Enter HTML here...</p>"
                  />
                  {/* HTML Preview */}
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#666', 
                      marginBottom: '4px',
                      fontWeight: '600'
                    }}>
                      Preview:
                    </div>
                    <div
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#fafafa',
                        minHeight: '60px',
                        maxHeight: '400px',
                        overflowX: 'auto',
                        overflowY: 'auto',
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div 
                        style={{ 
                          maxWidth: '100%',
                          overflowWrap: 'break-word',
                          wordWrap: 'break-word'
                        }}
                        dangerouslySetInnerHTML={{ __html: `<style>.amp-preview table { width: 100% !important; max-width: 100% !important; table-layout: fixed; } .amp-preview img { max-width: 100% !important; height: auto !important; }</style><div class="amp-preview">${linkifyHtml(htmlContent) || '<em style="color: #999;">No content</em>'}</div>` }}
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Save/Cancel buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end',
                paddingTop: '1rem',
                borderTop: '1px solid rgba(37, 99, 235, 0.2)'
              }}>
                <button
                  onClick={cancelEditingDescription}
                  disabled={savingDescription || generatingDescription}
                  style={{
                    background: 'rgba(108, 117, 125, 0.1)',
                    color: '#6c757d',
                    border: '2px solid rgba(108, 117, 125, 0.3)',
                    borderRadius: '10px',
                    padding: '0.75rem 1.5rem',
                    cursor: (savingDescription || generatingDescription) ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={generateDescription}
                  disabled={savingDescription || generatingDescription}
                  style={{
                    background: generatingDescription 
                      ? 'rgba(52, 168, 83, 0.5)' 
                      : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0.75rem 1.5rem',
                    cursor: (savingDescription || generatingDescription) ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {generatingDescription ? 'Generating...' : 'Generate'}
                </button>
                <button
                  onClick={saveDescriptionChanges}
                  disabled={savingDescription || generatingDescription}
                  style={{
                    background: savingDescription 
                      ? 'rgba(37, 99, 235, 0.5)' 
                      : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0.75rem 1.5rem',
                    cursor: (savingDescription || generatingDescription) ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {savingDescription ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            // Display mode - show rendered HTML with tasteful color styling
            <div
              className="amp-description-display"
              style={{ 
                color: '#333',
                fontSize: '1rem',
                lineHeight: '1.6',
                minHeight: '100px',
                padding: '1.5rem',
                background: 'rgba(37, 99, 235, 0.03)',
                borderRadius: '10px',
                border: '1px solid rgba(37, 99, 235, 0.1)'
              }}
              onClick={handleWikiImageClick}
              dangerouslySetInnerHTML={{
                __html: linkifyHtml(getDescriptionHTML(amplifier?.description)) || '<em style="color: #999;">No description available</em>'
              }}
            />
          )}

          {/* Wiki Suggest Edit — community contributions */}
          <div style={{ marginTop: '1rem' }}>
            <WikiSuggestEdit
              amplifierId={resolvedAmplifierId}
              currentDescription={getDescriptionHTML(amplifier?.description)}
              rawDescription={amplifier?.description}
              isOwner={isOwner}
              onEditApplied={refreshAmplifier}
              autoResubmitRevisionId={searchParams.get('wiki_resubmit')}
              onResubmitHandled={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('wiki_resubmit');
                setSearchParams(newParams, { replace: true });
              }}
              showHistoryToggle={false}
            />
          </div>
        </div>

        {/* Wiki Community Sections — only shown when content exists or user is authenticated */}
        {(() => {
          const WIKI_SECTIONS = [
            { type: 'maintenance', label: 'Maintenance & Tips', icon: '🔧', description: 'Bias procedures, common issues, modifications, and repair guides' },
            { type: 'variants', label: 'Variants & Revisions', icon: '🔀', description: 'Circuit changes, limited editions, and regional differences' },
            { type: 'notable_users', label: 'Notable Users', icon: '🎸', description: 'Famous players associated with this amplifier' },
            { type: 'amp_builders', label: 'Amp Builders', icon: '🛠️', description: 'Kits, parts suppliers, schematics, and wiring diagrams for builders' },
          ];
          const sectionsWithContent = WIKI_SECTIONS.filter(s => wikiSections[s.type]);
          const sectionsWithoutContent = WIKI_SECTIONS.filter(s => !wikiSections[s.type]);
          const hasAnySections = sectionsWithContent.length > 0;
          const showContributePrompt = isAuthenticated && sectionsWithoutContent.length > 0;

          if (!hasAnySections && !showContributePrompt) return null;

          return (
            <>
              {/* Sections with content */}
              {sectionsWithContent.map(section => {
                const data = wikiSections[section.type];
                const rawContent = data?.content || '';
                const htmlContent = prepareWikiHtml(
                  rawContent.startsWith('__RAW_HTML__')
                    ? rawContent.replace('__RAW_HTML__', '')
                    : rawContent
                );
                const isExpanded = wikiSectionsExpanded[section.type] !== false; // default expanded

                return (
                  <div key={section.type} style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '0',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    marginBottom: '1rem',
                    overflow: 'hidden'
                  }}>
                    {/* Section header — clickable to collapse/expand */}
                    <div
                      onClick={() => setWikiSectionsExpanded(prev => ({
                        ...prev,
                        [section.type]: !isExpanded
                      }))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        cursor: 'pointer',
                        borderBottom: isExpanded ? '1px solid #e9ecef' : 'none',
                        background: 'rgba(37, 99, 235, 0.02)',
                        transition: 'background 0.2s ease',
                        userSelect: 'none'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(37, 99, 235, 0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(37, 99, 235, 0.02)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.2rem' }}>{section.icon}</span>
                        <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem', fontWeight: '600' }}>
                          {section.label}
                        </h3>
                        <span style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          padding: '0.15rem 0.4rem',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Community
                        </span>
                      </div>
                      <span style={{ color: '#9ca3af', fontSize: '1.2rem', transition: 'transform 0.2s ease', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        ▾
                      </span>
                    </div>

                    {/* Section content */}
                    {isExpanded && (
                      <div style={{ padding: '1.5rem' }}>
                        <div
                          className="amp-description-display"
                          style={{
                            color: '#333',
                            fontSize: '1rem',
                            lineHeight: '1.6'
                          }}
                          onClick={handleWikiImageClick}
                          dangerouslySetInnerHTML={{ __html: linkifyHtml(htmlContent) }}
                        />
                        <div style={{ marginTop: '0.75rem' }}>
                          <WikiSuggestEdit
                            amplifierId={resolvedAmplifierId}
                            sectionType={section.type}
                            currentContent={htmlContent}
                            rawContent={rawContent}
                            isOwner={isOwner}
                            onEditApplied={() => { refreshAmplifier(); fetchWikiSections(); }}
                            showPendingReview={false}
                            showHistoryToggle={false}
                            compact
                          />
                        </div>
                        {data.editor_display_name && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            Last edited by {data.editor_display_name}
                            {parseInt(data.editor_trust_level) >= 1 && (
                              <WikiBadge trustLevel={parseInt(data.editor_trust_level)} />
                            )}
                            <span>· {new Date(data.last_edited_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Contribute prompt for empty sections */}
              {showContributePrompt && (
                <div style={{
                  background: 'white',
                  borderRadius: '10px',
                  padding: '1.25rem 1.5rem',
                  border: '1px dashed #d1d5db',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>✏️</span>
                    <div>
                      <div style={{ fontWeight: '600', color: '#374151', fontSize: '0.95rem', marginBottom: '0.3rem' }}>
                        Help improve this amp's page
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                        Know something about this amp that isn't covered above? You can contribute to these sections:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {sectionsWithoutContent.map(section => (
                          <WikiSuggestEdit
                            key={section.type}
                            amplifierId={resolvedAmplifierId}
                            sectionType={section.type}
                            currentContent=""
                            rawContent=""
                            isOwner={isOwner}
                            onEditApplied={() => { refreshAmplifier(); fetchWikiSections(); }}
                            showPendingReview={false}
                            showHistoryToggle={false}
                            compact
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Artifacts Section */}
        <div className="ads-section-card" style={{
          background: 'white',
          borderRadius: '10px',
          padding: '2rem',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          marginBottom: '2rem'
        }}>
          <div style={{
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #2563eb'
          }}>
            <h2 style={{
              margin: '0',
              color: '#333',
              fontSize: '1.8rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Amplifier Artifacts
            </h2>
          </div>

          {/* Artifact Type Tabs */}
          {artifacts.length > 0 && (() => {
            const counts = getArtifactTypeCounts();
            const typesPresent = Object.keys(counts);
            // Only show tabs if there's more than one type or any non-photo type
            const showTabs = typesPresent.length > 1 || (typesPresent.length === 1 && typesPresent[0] !== 'photo');
            if (!showTabs) return null;
            return (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem',
                marginBottom: '1.5rem',
                borderBottom: '1px solid #e9ecef',
                paddingBottom: '0.5rem'
              }}>
                <button
                  onClick={() => setArtifactTab('all')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderBottom: artifactTab === 'all' ? '3px solid #2563eb' : '3px solid transparent',
                    background: artifactTab === 'all' ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                    color: artifactTab === 'all' ? '#2563eb' : '#666',
                    fontWeight: artifactTab === 'all' ? '700' : '500',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    borderRadius: '6px 6px 0 0',
                    transition: 'all 0.2s'
                  }}
                >
                  All ({artifacts.length})
                </button>
                {Object.entries(ARTIFACT_TYPE_CONFIG).map(([type, config]) => {
                  const count = counts[type];
                  if (!count) return null;
                  return (
                    <button
                      key={type}
                      onClick={() => setArtifactTab(type)}
                      style={{
                        padding: '0.5rem 1rem',
                        border: 'none',
                        borderBottom: artifactTab === type ? '3px solid #2563eb' : '3px solid transparent',
                        background: artifactTab === type ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                        color: artifactTab === type ? '#2563eb' : '#666',
                        fontWeight: artifactTab === type ? '700' : '500',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        borderRadius: '6px 6px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      {config.icon} {config.label} ({count})
                    </button>
                  );
                })}
              </div>
            );
          })()}
          
          {!readOnlyMode && (
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!readOnlyMode && !uploading) setArtifactDragOver(true); }}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!readOnlyMode && !uploading) setArtifactDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setArtifactDragOver(false); }}
              onDrop={handleArtifactDrop}
              style={{
                background: artifactDragOver ? '#e8f4fd' : '#f8f9fa',
                borderRadius: '8px',
                padding: '1.5rem',
                border: artifactDragOver ? '2px dashed #74b9ff' : '1px solid #e9ecef',
                marginBottom: '1.5rem',
                transition: 'background 0.15s, border 0.15s'
              }}
            >
              {artifactDragOver && (
                <div style={{ textAlign: 'center', color: '#0984e3', fontWeight: '600', marginBottom: '0.75rem', pointerEvents: 'none' }}>
                  Drop files to upload as {uploadArtifactType.replace('_', ' ')}
                </div>
              )}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={uploadArtifactType}
                  onChange={(e) => setUploadArtifactType(e.target.value)}
                  style={{
                    padding: '0.6rem 1rem',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    background: 'white',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    minWidth: '160px'
                  }}
                >
                  <option value="photo">📷 Photo</option>
                  <option value="schematic">📐 Schematic</option>
                  <option value="wiring_diagram">🔌 Wiring Diagram</option>
                  <option value="layout">📏 Layout</option>
                  <option value="parts_list">📋 Parts List</option>
                  <option value="manual">📖 Manual</option>
                  <option value="voltage_chart">⚡ Voltage Chart</option>
                  <option value="youtube">▶️ YouTube Video</option>
                  <option value="audio">🎵 Audio Clip</option>
                  <option value="other">📁 Other</option>
                </select>
                {uploadArtifactType === 'youtube' ? (
                  <>
                    <input
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleYouTubeAdd()}
                      disabled={!resolvedAmplifierId || uploading}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        padding: '0.8rem',
                        border: '2px dashed rgba(255, 0, 0, 0.3)',
                        borderRadius: '6px',
                        background: 'white',
                        fontSize: '0.95rem'
                      }}
                    />
                    <button
                      onClick={handleYouTubeAdd}
                      disabled={!resolvedAmplifierId || uploading || !youtubeUrl.trim()}
                      style={{
                        padding: '0.8rem 1.2rem',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#cc0000',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ▶️ Add Video
                    </button>
                  </>
                ) : (
                  <input
                    type="file"
                    accept="image/*,.svg,image/svg+xml,application/pdf,.xlsx,.xls,.csv,.ods,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/vnd.oasis.opendocument.spreadsheet,audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/webm,.mp3,.wav,.ogg,.m4a"
                    multiple
                    disabled={!resolvedAmplifierId || uploading}
                    onChange={handleArtifactUpload}
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      padding: '0.8rem',
                      border: '2px dashed rgba(116, 185, 255, 0.3)',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.95rem'
                    }}
                  />
                )}
              </div>
              {uploading && (
                <div style={{
                  marginTop: '1rem',
                  color: '#888',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  Uploading...
                </div>
              )}
            </div>
          )}
          
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
          {getFilteredArtifacts().map((artifact, idx) => (
            <div key={artifact.artifact_id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120 }}>
              {/* Thumbnail */}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                {artifact.mime_type && (artifact.mime_type.startsWith('image/') || isSpreadsheet(artifact.mime_type)) ? (
                  <img
                    src={`${API_URL}/amplifier_artifacts/${artifact.artifact_id}/thumbnail`}
                    alt={artifact.filename}
                    style={{
                      width: 120, height: 120, objectFit: 'cover',
                      border: amplifier?.pinned_artifact_id === artifact.artifact_id ? '3px solid #2563eb' : '1px solid #ccc',
                      borderRadius: '4px 4px 0 0', cursor: 'pointer'
                    }}
                    onClick={() => openViewer(getGlobalIndex(idx))}
                    title={`${(ARTIFACT_TYPE_CONFIG[artifact.artifact_type] || ARTIFACT_TYPE_CONFIG.photo).icon} ${(ARTIFACT_TYPE_CONFIG[artifact.artifact_type] || ARTIFACT_TYPE_CONFIG.photo).label} — Click to view`}
                  />
                ) : artifact.mime_type === 'application/pdf' ? (
                  <PdfThumbnail
                    artifactUrl={`${API_URL}/amplifier_artifacts/${artifact.artifact_id}`}
                    width={120}
                    onClick={() => openViewer(getGlobalIndex(idx))}
                    style={{
                      border: amplifier?.pinned_artifact_id === artifact.artifact_id ? '3px solid #2563eb' : '1px solid #ccc',
                      borderRadius: '4px 4px 0 0',
                    }}
                  />
                ) : artifact.artifact_type === 'youtube' && artifact.external_url ? (
                  <div style={{ position: 'relative', width: 120, height: 120 }}>
                    <img
                      src={`https://img.youtube.com/vi/${extractYouTubeId(artifact.external_url)}/hqdefault.jpg`}
                      alt="YouTube video thumbnail"
                      loading="lazy"
                      style={{
                        width: 120, height: 120, objectFit: 'cover',
                        border: '1px solid #ccc', borderRadius: '4px 4px 0 0', cursor: 'pointer'
                      }}
                      onClick={() => openViewer(getGlobalIndex(idx))}
                      title="▶️ Videos — Click to view"
                    />
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', pointerEvents: 'none'
                    }}>
                      <span style={{
                        fontSize: '28px', lineHeight: 1,
                        filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))'
                      }}>▶️</span>
                    </div>
                  </div>
                ) : artifact.mime_type && artifact.mime_type.startsWith('audio/') ? (
                  <button
                    onClick={() => openViewer(getGlobalIndex(idx))}
                    style={{
                      width: 120, height: 120,
                      border: '1px solid #ccc',
                      borderRadius: '4px 4px 0 0',
                      cursor: 'pointer', background: '#f0f4ff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: '6px', padding: '8px', overflow: 'hidden'
                    }}
                    title="🎵 Audio — Click to play"
                  >
                    <span style={{ fontSize: '32px', lineHeight: 1 }}>🎵</span>
                    <span style={{ fontSize: '0.6rem', color: '#555', textAlign: 'center', wordBreak: 'break-all' }}>
                      {artifact.filename}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => openViewer(getGlobalIndex(idx))}
                    style={{
                      width: 120, height: 120,
                      border: amplifier?.pinned_artifact_id === artifact.artifact_id ? '3px solid #2563eb' : '1px solid #ccc',
                      borderRadius: '4px 4px 0 0',
                      cursor: 'pointer', background: '#f5f5f5',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: '6px', padding: '8px', overflow: 'hidden'
                    }}
                    title={`${(ARTIFACT_TYPE_CONFIG[artifact.artifact_type] || ARTIFACT_TYPE_CONFIG.photo).icon} ${(ARTIFACT_TYPE_CONFIG[artifact.artifact_type] || ARTIFACT_TYPE_CONFIG.photo).label} — Click to view`}
                  >
                    <span style={{ fontSize: '32px', lineHeight: 1 }}>📁</span>
                    <span style={{ fontSize: '0.6rem', color: '#555', textAlign: 'center', wordBreak: 'break-all' }}>
                      {artifact.filename}
                    </span>
                  </button>
                )}
                {/* Pinned badge */}
                {amplifier?.pinned_artifact_id === artifact.artifact_id && (
                  <span style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    background: '#2563eb',
                    color: 'white',
                    fontSize: '0.6rem',
                    fontWeight: '700',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    pointerEvents: 'none',
                    letterSpacing: '0.03em'
                  }}>
                    📌 PINNED
                  </span>
                )}
                {/* Type badge on thumbnail (read-only view) */}
                {artifact.artifact_type && artifact.artifact_type !== 'photo' && readOnlyMode && (
                  <span style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    fontSize: '0.65rem',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    pointerEvents: 'none'
                  }}>
                    {(ARTIFACT_TYPE_CONFIG[artifact.artifact_type] || {}).icon}
                  </span>
                )}
                {/* Delete button overlay */}
                {!readOnlyMode && (
                  <button
                    onClick={() => handleArtifactDelete(artifact.artifact_id)}
                    style={{
                      position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%', cursor: 'pointer'
                    }}
                    title="Delete Artifact"
                  >✕</button>
                )}
                {/* Pin/unpin button overlay (image artifacts only, edit mode) */}
                {!readOnlyMode && artifact.mime_type && artifact.mime_type.startsWith('image/') && (
                  <button
                    onClick={() => handlePinArtifact(artifact.artifact_id)}
                    style={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      background: amplifier?.pinned_artifact_id === artifact.artifact_id ? '#2563eb' : 'rgba(255,255,255,0.8)',
                      color: amplifier?.pinned_artifact_id === artifact.artifact_id ? 'white' : '#555',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      padding: '2px 5px',
                      lineHeight: '1',
                      transition: 'all 0.2s ease'
                    }}
                    title={amplifier?.pinned_artifact_id === artifact.artifact_id ? 'Unpin hero image' : 'Pin as hero image'}
                  >
                    📌
                  </button>
                )}
              </div>
              {/* Type selector below thumbnail */}
              {!readOnlyMode && (
                <select
                  value={artifact.artifact_type || 'photo'}
                  onChange={(e) => handleArtifactTypeChange(artifact.artifact_id, e.target.value)}
                  title="Change artifact type"
                  style={{
                    width: 120,
                    background: '#f8f9fa',
                    border: '1px solid #ccc',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    fontSize: '0.75rem',
                    padding: '3px 4px',
                    cursor: 'pointer',
                    color: '#555'
                  }}
                >
                  <option value="photo">📷 Photo</option>
                  <option value="schematic">📐 Schematic</option>
                  <option value="wiring_diagram">🔌 Wiring Diagram</option>
                  <option value="layout">📏 Layout</option>
                  <option value="parts_list">📋 Parts List</option>
                  <option value="manual">📖 Manual</option>
                  <option value="voltage_chart">⚡ Voltage Chart</option>
                  <option value="other">📁 Other</option>
                </select>
              )}
            </div>
          ))}
          {getFilteredArtifacts().length === 0 && artifacts.length > 0 && (
            <p style={{ color: '#999', fontStyle: 'italic', margin: '1rem 0' }}>
              No {(ARTIFACT_TYPE_CONFIG[artifactTab] || {}).label || artifactTab} artifacts for this amplifier.
            </p>
          )}
        </div>
        {/* Artifact Viewer Popup */}
        {viewerOpen && viewerIndex >= 0 && (
          <ArtifactViewer
            artifacts={artifacts}
            currentIndex={viewerIndex}
            onClose={closeViewer}
            onPrev={prevViewer}
            onNext={nextViewer}
            amplifierId={resolvedAmplifierId}
            ampUrl={amplifier ? getAmpUrl(amplifier) : null}
          />
        )}
      </div>

      {/* Child Amplifier Management Section */}
      {resolvedAmplifierId && amplifier && amplifier.is_parent && (
        <div style={{ 
          marginTop: '2rem',
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '10px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ 
              margin: 0, 
              color: '#333',
            fontSize: '1.5rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Individual Units ({children.length})
            </h3>
            {!readOnlyMode && (
              <button
                onClick={handleCreateChild}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                }}
              >
                + Add Individual Unit
              </button>
            )}
          </div>
          
          {children.length === 0 ? (
            <div style={{ 
              background: '#f0f0f0',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ 
                color: '#555', 
                fontStyle: 'italic',
                fontSize: '1.1rem',
                margin: 0,
                lineHeight: '1.6'
              }}>
                No individual units created yet. This parent amplifier represents the specification/model.<br/>
                Add individual units to track specific manufactured amplifiers with serial numbers, artifacts, and component variations.
              </p>
            </div>
          ) : (
            <div style={{ 
              background: 'white',
              borderRadius: '8px',
              padding: '1rem',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              overflowX: 'auto'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                fontSize: '0.95rem',
                color: '#333'
              }}>
                <thead>
                  <tr style={{ 
                    background: '#2563eb',
                    color: 'white'
                  }}>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>Name</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>Serial Number</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>Year Range</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>Model Variant</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}>Created</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      border: 'none',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((child, index) => (
                    <tr key={child.amplifier_id} style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none',
                        color: '#333',
                        fontWeight: '500'
                      }}>
                        {child.name || 'Unnamed Unit'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none', 
                        fontFamily: 'monospace',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        {child.serial_number || <em style={{ color: '#666' }}>No S/N</em>}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none',
                        color: '#333'
                      }}>
                        {formatYearRange(child)}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none',
                        color: '#333'
                      }}>
                        {child.model || <em style={{ color: '#666' }}>Same as parent</em>}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none',
                        color: '#333'
                      }}>
                        {child.created_at ? new Date(child.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        border: 'none',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={() => window.open(`/amplifier-components/${child.amplifier_id}`, '_blank')}
                          style={{
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.8rem',
                            cursor: 'pointer',
                            marginRight: '0.5rem',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          View
                        </button>
                        {!readOnlyMode && (
                          <>
                            <button
                              onClick={() => navigate(`/amplifier_screen`, { state: { editAmplifier: child } })}
                              style={{
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.4rem 0.8rem',
                                cursor: 'pointer',
                                marginRight: '0.5rem',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteChild(child.amplifier_id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.4rem 0.8rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {readOnlyMode && (
                          <span style={{ 
                            color: '#666', 
                            fontStyle: 'italic', 
                            fontSize: '0.8rem',
                            marginLeft: '0.5rem',
                            background: 'rgba(108, 117, 125, 0.1)',
                            padding: '0.2rem 0.6rem',
                            borderRadius: '4px'
                          }}>
                            View Only
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Unified Edit History — all wiki sections + parts list */}
      <div style={{ marginTop: '2rem' }}>
        <button
          onClick={() => {
            if (!showAmpHistory) fetchAmpHistory();
            setShowAmpHistory(v => !v);
          }}
          style={{
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '0.4rem 0.8rem',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
        >
          📜 {showAmpHistory ? 'Hide Change History' : 'View Change History'}
        </button>

        {showAmpHistory && (() => {
          const SECTION_LABELS = {
            description: '📝 Description',
            maintenance: '🔧 Maintenance',
            variants: '🔀 Variants',
            notable_users: '🎸 Notable Users',
            amp_builders: '🛠️ Amp Builders',
          };
          return (
            <div style={{
              marginTop: '0.75rem',
              background: '#f9fafb',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              padding: '1rem',
              maxHeight: '360px',
              overflowY: 'auto',
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem' }}>
                Change History
              </h4>
              {ampHistoryLoading ? (
                <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>Loading…</p>
              ) : ampHistory.length === 0 ? (
                <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>No edit history yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {ampHistory.map((entry, i) => (
                    <div key={`${entry.change_type}-${entry.id}-${i}`} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0.75rem',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      flexWrap: 'wrap',
                    }}>
                      {/* Section label */}
                      <span style={{
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.72rem',
                        fontWeight: '600',
                        background: entry.change_type === 'parts_list' ? '#f0fdf4' : '#eff6ff',
                        color: entry.change_type === 'parts_list' ? '#166534' : '#1e40af',
                        whiteSpace: 'nowrap',
                      }}>
                        {entry.change_type === 'parts_list' ? '🔩 Parts List' : (SECTION_LABELS[entry.section_type] || entry.section_type)}
                      </span>
                      {/* Status badge */}
                      <span style={{
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.72rem',
                        fontWeight: '600',
                        background: entry.status === 'approved' ? '#d1fae5' : entry.status === 'rejected' ? '#fee2e2' : entry.status === 'changes_requested' ? '#e0e7ff' : '#fef3c7',
                        color: entry.status === 'approved' ? '#065f46' : entry.status === 'rejected' ? '#991b1b' : entry.status === 'changes_requested' ? '#3730a3' : '#92400e',
                        whiteSpace: 'nowrap',
                      }}>
                        {entry.status === 'changes_requested' ? 'changes req.' : entry.status}
                      </span>
                      {/* Summary + author */}
                      <span style={{ color: '#374151', flex: 1, minWidth: 0 }}>
                        <strong>{entry.author_display_name}</strong>: {entry.edit_summary}
                      </span>
                      {/* Date */}
                      <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                        {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Where to Buy */}
      {amplifier?.manufacturer_name && (amplifier?.name || amplifier?.model) && (
        <div style={{
          marginTop: '2rem',
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '10px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          <WhereToBuy
            manufacturer={amplifier.manufacturer_name}
            model={amplifier.name || amplifier.model}
            ampId={resolvedAmplifierId}
            token={token}
          />
        </div>
      )}

      {/* Community Parts List */}
        {resolvedAmplifierId && amplifier && (
          <div ref={partsListRef} style={{ 
            marginTop: '2rem',
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            padding: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <AmpPartsList amplifierId={resolvedAmplifierId} isOwner={isOwner} isAdmin={isAdmin} autoShowPending={!!reviewPartsParam} />
          </div>
        )}

      {/* Template-Based Component Assignment (Admin only) */}
        {isAdmin && resolvedAmplifierId && amplifier && (
          <div style={{ 
            marginTop: '2rem',
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            padding: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <AmplifierTemplateComponents amplifierId={resolvedAmplifierId} readOnly={readOnlyMode} />
          </div>
        )}

      {/* Discussion Section */}
      {resolvedAmplifierId && amplifier && (
        <div style={{ 
          marginTop: '2rem',
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '10px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          <AmpDiscussion 
            amplifierId={resolvedAmplifierId} 
            amplifierName={amplifier?.name} 
          />
        </div>
      )}

      {/* Lightbox — full-size view for artifact thumbnails embedded in wiki content */}
      {lightboxArtifactId && (
        <div
          className="wiki-lightbox-overlay"
          onClick={() => setLightboxArtifactId(null)}
          onKeyDown={(e) => e.key === 'Escape' && setLightboxArtifactId(null)}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <button
            className="wiki-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightboxArtifactId(null); }}
            aria-label="Close image"
          >
            ✕
          </button>
          <img
            src={`${API_URL}/amplifier_artifacts/${lightboxArtifactId}`}
            alt="Full size"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default AmplifierDetailsScreen;